/**
 * ModuleSection — seccion editable de un modulo de identificacion MGA.
 *
 * Renderiza un modulo del documento vivo (uno de los 6 de identificacion)
 * con su estado de origen, contenido formateado con FormulationMarkdown,
 * edicion inline (via IA o manual), y los supuestos de la IA marcados
 * como "verifica".
 *
 * Un modulo con contenido ofrece "Confirmar" y "Editar" lado a lado. El
 * caso comun es que el borrador de la IA sirva tal cual: obligar a entrar
 * a edicion solo para poder confirmarlo era friccion sin proposito.
 *
 * Diseno: card blanca, flat, sin gradientes. Badges de estado (confirmado
 * = emerald, IA = brand-blue, pendiente = slate). Principio: honestidad
 * visual — el origen del contenido siempre es visible.
 */

import { useState } from "react";

import { InlineSpinner } from "@/components/InlineSpinner";
import type { ModuleDTO } from "@/lib/api/formulations";
import { MODULE_META } from "@/lib/api/formulations";

import { moduleAnchorId } from "@/pages/expediente/focus";

import { AssumptionsBadge } from "./AssumptionsBadge";
import { FormulationMarkdown } from "./FormulationMarkdown";

/** Literal que requiere atencion del equipo; se resalta en el contenido. */
const COMPLETE_MARKER = "[completar con tu equipo financiero]";

interface ModuleSectionProps {
  module: ModuleDTO;
  /** 1-indexed para el badge numerico. */
  index: number;
  onDraft: () => void;
  onSave: (content: string, status: "draft" | "confirmed") => void;
  isDrafting: boolean;
  /**
   * Hay un `onSave` en vuelo para ESTE modulo. El guardado tarda lo
   * suficiente como para que sin senal el usuario crea que el clic no
   * registro y vuelva a apretar.
   */
  isSaving?: boolean;
  isPro: boolean;
  /**
   * Mensaje legible del ultimo intento fallido de `onDraft` para ESTE
   * modulo (ej. el 503 "no se desconto tu cuota" cuando el modelo no
   * logra completar el borrador). `null`/`undefined` si no hay error o si
   * ya se maneja en otro lado (402 tier/cuota, 409 gate de preparacion).
   */
  draftError?: string | null;
}

// --- Badges de estado / origen ---

function StatusBadge({
  status,
  origin,
}: {
  status: ModuleDTO["status"];
  origin: ModuleDTO["origin"];
}) {
  if (status === "confirmed") {
    return (
      <span
        data-testid="module-status-confirmed"
        className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-0.5 text-[10px] font-semibold text-emerald-700"
      >
        <svg aria-hidden="true" viewBox="0 0 16 16" fill="currentColor" className="h-3 w-3">
          <path
            fillRule="evenodd"
            d="M12.416 3.376a.75.75 0 0 1 .208 1.04l-5 7.5a.75.75 0 0 1-1.154.114l-3-3a.75.75 0 0 1 1.06-1.06l2.353 2.353 4.493-6.74a.75.75 0 0 1 1.04-.207Z"
            clipRule="evenodd"
          />
        </svg>
        Confirmado
      </span>
    );
  }
  if (origin === "ai") {
    return (
      <span
        data-testid="module-status-ai"
        className="inline-flex items-center gap-1 rounded-full bg-brand-50 px-2.5 py-0.5 text-[10px] font-semibold text-brand-700"
      >
        <svg aria-hidden="true" viewBox="0 0 16 16" fill="currentColor" className="h-3 w-3">
          <path d="M7.657 6.247c.11-.33.576-.33.686 0l.645 1.937a2.89 2.89 0 0 0 1.829 1.828l1.936.645c.33.11.33.576 0 .686l-1.937.645a2.89 2.89 0 0 0-1.828 1.829l-.645 1.936a.361.361 0 0 1-.686 0l-.645-1.937a2.89 2.89 0 0 0-1.828-1.828l-1.937-.645a.361.361 0 0 1 0-.686l1.937-.645a2.89 2.89 0 0 0 1.828-1.829l.645-1.936Zm-3.861-1.37a.25.25 0 0 1 .475 0l.328.984a1.444 1.444 0 0 0 .914.914l.984.328a.25.25 0 0 1 0 .474l-.984.328a1.444 1.444 0 0 0-.914.914l-.328.984a.25.25 0 0 1-.474 0l-.328-.984a1.444 1.444 0 0 0-.914-.914l-.984-.328a.25.25 0 0 1 0-.474l.984-.328a1.444 1.444 0 0 0 .914-.914l.328-.984ZM9.38 1.106a.25.25 0 0 1 .474 0l.328.984a1.444 1.444 0 0 0 .914.914l.984.328a.25.25 0 0 1 0 .474l-.984.328a1.444 1.444 0 0 0-.914.914l-.328.984a.25.25 0 0 1-.474 0l-.328-.984a1.444 1.444 0 0 0-.914-.914l-.984-.328a.25.25 0 0 1 0-.474l.984-.328a1.444 1.444 0 0 0 .914-.914l.328-.984Z" />
        </svg>
        Sugerido por IA
      </span>
    );
  }
  return (
    <span
      data-testid="module-status-pending"
      className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-0.5 text-[10px] font-semibold text-slate-500"
    >
      Pendiente
    </span>
  );
}

function IndexBadge({ index, status }: { index: number; status: ModuleDTO["status"] }) {
  let cls = "inline-flex h-6 w-6 items-center justify-center rounded-full text-[11px] font-bold ";
  if (status === "confirmed") {
    cls += "bg-emerald-100 text-emerald-700";
  } else if (status === "draft") {
    cls += "bg-brand-100 text-brand-700";
  } else {
    cls += "bg-slate-100 text-slate-500";
  }
  return (
    <span aria-hidden="true" className={cls}>
      {index}
    </span>
  );
}

/**
 * Parte del contenido que puede contener `[completar con tu equipo financiero]`.
 * Si el texto lo contiene, resaltamos ese marcador en un span ambar mono
 * para que sea inmediatamente visible.
 */
function ContentWithMarkers({ source }: { source: string }) {
  if (!source.includes(COMPLETE_MARKER)) {
    return <FormulationMarkdown source={source} />;
  }
  // Partimos el texto por el marcador y lo re-ensamblamos con un span.
  const parts = source.split(COMPLETE_MARKER);
  return (
    <div>
      {parts.map((part, idx) => (
        <span key={idx}>
          <FormulationMarkdown source={part} />
          {idx < parts.length - 1 && (
            <span className="rounded bg-amber-100 px-1 font-mono text-xs text-amber-800">
              {COMPLETE_MARKER}
            </span>
          )}
        </span>
      ))}
    </div>
  );
}

export function ModuleSection({
  module,
  index,
  onDraft,
  onSave,
  isDrafting,
  isSaving = false,
  isPro,
  draftError = null,
}: ModuleSectionProps) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(module.content ?? "");
  const meta = MODULE_META[module.key as keyof typeof MODULE_META];
  const isEmpty = module.status === "empty" || !module.content;

  // Sincronizamos el draft local cuando el modulo se actualiza desde
  // arriba (ej. after a successful draftModule call).
  // Usamos una key en el textarea para que React lo resetee si el
  // contenido cambia mientras no esta editando.
  const hasContent = !isEmpty;

  function handleStartEdit() {
    setDraft(module.content ?? "");
    setEditing(true);
  }

  function handleCancelEdit() {
    setEditing(false);
    setDraft(module.content ?? "");
  }

  return (
    <article
      id={moduleAnchorId(module.key)}
      data-testid={`module-section-${module.key}`}
      /* scrollMarginTop compensa la altura de la FocusBar sticky: sin eso
         el modulo queda tapado por la franja al hacer scroll hasta el. */
      style={{ scrollMarginTop: "5rem" }}
      className="rounded-xl border border-slate-200 bg-white p-4"
    >
      {/* Header */}
      <div className="mb-3 flex flex-wrap items-start justify-between gap-2">
        <div className="flex items-center gap-2">
          <IndexBadge index={index} status={module.status} />
          <div>
            <p className="text-sm font-medium text-slate-900">{meta?.label ?? module.key}</p>
            <p className="text-xs text-slate-400">{meta?.question}</p>
          </div>
        </div>
        <StatusBadge status={module.status} origin={module.origin} />
      </div>

      {/* Cuerpo: vacio, drafting, editando, o con contenido.
          `editing` se chequea ANTES que `isEmpty`: "Describir manualmente"
          entra a edicion desde un modulo vacio (isEmpty=true) y debe
          mostrar EditingBody, no volver a EmptyModuleBody. Esto es seguro
          para el camino pre-existente de "Editar" tambien — ese boton solo
          vive en ReadBody, que ya requeria !isEmpty, asi que editing=true
          implicaba isEmpty=false de todas formas. */}
      {isDrafting ? (
        <div
          data-testid="module-drafting"
          className="flex items-center gap-2 rounded-lg bg-slate-50 p-4 text-sm text-slate-600"
        >
          <InlineSpinner tone="brand" />
          <span>La IA está redactando este módulo...</span>
        </div>
      ) : editing ? (
        <EditingBody
          content={draft}
          onChange={setDraft}
          onSave={(status) => {
            onSave(draft, status);
            setEditing(false);
          }}
          onCancel={handleCancelEdit}
        />
      ) : isEmpty ? (
        <EmptyModuleBody
          onDraft={onDraft}
          onManualEntry={handleStartEdit}
          isPro={isPro}
          draftError={draftError}
        />
      ) : (
        <ReadBody
          content={module.content}
          status={module.status}
          isSaving={isSaving}
          onEdit={handleStartEdit}
          onConfirm={() => onSave(module.content, "confirmed")}
        />
      )}

      {/* Supuestos */}
      {!editing && hasContent && <AssumptionsBadge assumptions={module.assumptions} />}
    </article>
  );
}

// --- Sub-componentes del cuerpo ---

function EmptyModuleBody({
  onDraft,
  onManualEntry,
  isPro,
  draftError,
}: {
  onDraft: () => void;
  /** Abre el editor en blanco para escribir el modulo a mano — mismo
   * camino que "Editar" (`handleStartEdit`), sin gate de Pro: es texto
   * del usuario, no una redaccion de IA. */
  onManualEntry: () => void;
  isPro: boolean;
  draftError?: string | null;
}) {
  return (
    <div
      data-testid="module-empty-body"
      className="flex flex-col items-start gap-3 rounded-lg border border-dashed border-slate-200 bg-slate-50 p-4"
    >
      {/* Sin Pro no se ofrece la IA en el texto: su botón está bloqueado
          al lado (issue #629). */}
      <p className="text-xs text-slate-500">
        {isPro
          ? "Este módulo aún no tiene contenido. Puedes pedirle a la IA que lo redacte basándose en los datos reales de tu municipio, o escribirlo tú mismo."
          : "Este módulo aún no tiene contenido. Escríbelo con la guía y los datos reales de tu municipio."}
      </p>
      <div className="flex flex-wrap items-start gap-2">
        {isPro ? (
          <button
            type="button"
            data-testid="module-draft-btn"
            onClick={onDraft}
            className="min-h-[44px] rounded-lg border border-emerald-600 px-4 py-2 text-sm font-semibold text-emerald-700 transition-colors hover:bg-emerald-50 focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 focus:outline-hidden"
          >
            Ayúdame con este módulo
          </button>
        ) : (
          <div className="flex flex-col gap-1.5">
            <button
              type="button"
              disabled
              data-testid="module-draft-btn-locked"
              title="Disponible en Plan Pro"
              className="min-h-[44px] cursor-not-allowed rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-400 opacity-50"
            >
              Ayúdame con este módulo
            </button>
            <p className="text-xs text-slate-500">
              Requiere plan Pro.{" "}
              <a
                href="mailto:hola@kaptar.com.co?subject=Quiero%20subir%20a%20Pro"
                className="font-semibold text-brand-700 hover:underline"
              >
                Pedir upgrade
              </a>
            </p>
          </div>
        )}
        <button
          type="button"
          data-testid="module-manual-btn"
          onClick={onManualEntry}
          className="min-h-[44px] rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50 focus:ring-2 focus:ring-brand-500 focus:ring-offset-2 focus:outline-hidden"
        >
          Describir manualmente
        </button>
      </div>
      {draftError && (
        <p
          data-testid="module-draft-error"
          className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700"
        >
          {draftError}
        </p>
      )}
    </div>
  );
}

function ReadBody({
  content,
  status,
  isSaving,
  onEdit,
  onConfirm,
}: {
  content: string;
  status: ModuleDTO["status"];
  /** Hay un guardado en vuelo para ESTE modulo. */
  isSaving: boolean;
  onEdit: () => void;
  onConfirm: () => void;
}) {
  // Un modulo ya confirmado no vuelve a ofrecer "Confirmar": el badge de
  // estado ya lo dice y repetir la accion solo agrega ruido.
  const canConfirm = status !== "confirmed";
  // Mismo gate que en EditingBody. "Guardar borrador" no valida el
  // contenido, asi que un modulo puede llegar aca con solo espacios.
  const isBlankContent = content.trim().length === 0;
  const confirmDisabled = isBlankContent || isSaving;

  /**
   * El modulo todavia arrastra `[completar con tu equipo financiero]`.
   *
   * Confirmarlo asi no esta prohibido -- el marcador es precisamente el
   * mecanismo con el que la IA declara que ahi falta un dato real (ADR-020
   * / ADR-021), y quien decide cuando llenarlo es el equipo del muni, no
   * nosotros. Pero confirmar es el acto por el que el usuario se hace
   * cargo del texto, y hacerlo de un clic con un hueco declarado adentro
   * es como termina un placeholder radicado en PIIP.
   *
   * Por eso: no se bloquea, se pide un segundo clic deliberado.
   */
  const hasPendingMarker = content.includes(COMPLETE_MARKER);
  const [confirmingWithMarker, setConfirmingWithMarker] = useState(false);

  // Si el contenido deja de tener el marcador (el usuario lo lleno y
  // guardo), la confirmacion en dos pasos ya no aplica.
  const [prevContent, setPrevContent] = useState(content);
  if (prevContent !== content) {
    setPrevContent(content);
    setConfirmingWithMarker(false);
  }

  function handleConfirmClick() {
    if (hasPendingMarker && !confirmingWithMarker) {
      setConfirmingWithMarker(true);
      return;
    }
    onConfirm();
  }

  return (
    <div data-testid="module-read-body">
      <div className="rounded-lg bg-slate-50 p-3 text-sm text-slate-900">
        <ContentWithMarkers source={content} />
      </div>

      {canConfirm && hasPendingMarker && !isSaving && (
        <p
          data-testid="module-pending-marker-warning"
          className="mt-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800"
        >
          {confirmingWithMarker
            ? "Vas a confirmar este módulo con un dato sin completar. Queda marcado en el documento para que tu equipo lo resuelva antes de radicar."
            : "Este módulo tiene un dato pendiente de tu equipo financiero. Puedes completarlo desde “Editar” o confirmarlo así y resolverlo después."}
        </p>
      )}

      <div className="mt-2 flex flex-wrap gap-2">
        {canConfirm && (
          <button
            type="button"
            data-testid="module-confirm-read-btn"
            onClick={handleConfirmClick}
            disabled={confirmDisabled}
            aria-busy={isSaving}
            title={isBlankContent ? "Escribe el contenido antes de confirmar" : undefined}
            className={`inline-flex min-h-[44px] items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold transition-colors focus:ring-2 focus:ring-offset-2 focus:outline-hidden ${
              isBlankContent
                ? "cursor-not-allowed bg-slate-200 text-slate-400 opacity-50"
                : confirmingWithMarker
                  ? "bg-amber-600 text-white hover:bg-amber-700 focus:ring-amber-600"
                  : "bg-cta-500 text-white hover:bg-cta-600 focus:ring-cta-500"
            } ${isSaving ? "cursor-wait" : ""}`}
          >
            {isSaving && <InlineSpinner tone="white" />}
            {isSaving
              ? "Confirmando…"
              : confirmingWithMarker
                ? "Confirmar con el dato pendiente"
                : "Confirmar"}
          </button>
        )}
        {confirmingWithMarker && !isSaving && (
          <button
            type="button"
            data-testid="module-cancel-marker-confirm-btn"
            onClick={() => setConfirmingWithMarker(false)}
            className="min-h-[44px] rounded-lg px-4 py-2 text-sm font-medium text-slate-500 transition-colors hover:text-slate-700 focus:ring-2 focus:ring-slate-400 focus:ring-offset-2 focus:outline-hidden"
          >
            Cancelar
          </button>
        )}
        {/*
          "Editar" desaparece mientras el guardado esta en vuelo. Entrar a
          edicion en ese momento abriria el textarea con el contenido que el
          servidor esta a punto de reemplazar, y al llegar la respuesta el
          usuario tendria dos versiones del modulo sin saber cual gana.
          Ocultarlo deja una sola accion posible: esperar.
        */}
        {!isSaving && (
          <button
            type="button"
            data-testid="module-edit-btn"
            onClick={onEdit}
            className="min-h-[44px] rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50 focus:ring-2 focus:ring-brand-500 focus:ring-offset-2 focus:outline-hidden"
          >
            Editar
          </button>
        )}
      </div>
    </div>
  );
}

function EditingBody({
  content,
  onChange,
  onSave,
  onCancel,
}: {
  content: string;
  onChange: (val: string) => void;
  onSave: (status: "draft" | "confirmed") => void;
  onCancel: () => void;
}) {
  const isEmptyContent = content.trim().length === 0;

  return (
    <div data-testid="module-editing-body" className="flex flex-col gap-3">
      <label className="sr-only" htmlFor="module-textarea">
        Editar contenido del módulo
      </label>
      <textarea
        id="module-textarea"
        data-testid="module-textarea"
        value={content}
        onChange={(e) => onChange(e.target.value)}
        rows={6}
        className="w-full resize-y rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900 focus:border-brand-500 focus:ring-1 focus:ring-brand-500 focus:outline-hidden"
        placeholder="Escribe el contenido de este módulo..."
      />
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          data-testid="module-confirm-btn"
          onClick={() => onSave("confirmed")}
          disabled={isEmptyContent}
          title={isEmptyContent ? "Escribe el contenido antes de confirmar" : undefined}
          className={`min-h-[44px] rounded-lg px-4 py-2 text-sm font-semibold transition-colors focus:ring-2 focus:ring-offset-2 focus:outline-hidden ${
            isEmptyContent
              ? "cursor-not-allowed bg-slate-200 text-slate-400 opacity-50"
              : "bg-cta-500 text-white hover:bg-cta-600 focus:ring-cta-500"
          }`}
        >
          Confirmar
        </button>
        <button
          type="button"
          data-testid="module-save-btn"
          onClick={() => onSave("draft")}
          className="min-h-[44px] rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50 focus:ring-2 focus:ring-brand-500 focus:ring-offset-2 focus:outline-hidden"
        >
          Guardar borrador
        </button>
        <button
          type="button"
          data-testid="module-cancel-btn"
          onClick={onCancel}
          className="min-h-[44px] rounded-lg px-4 py-2 text-sm font-medium text-slate-500 hover:text-slate-700 focus:ring-2 focus:ring-slate-400 focus:ring-offset-2 focus:outline-hidden"
        >
          Cancelar
        </button>
      </div>
    </div>
  );
}
